import type { LookScheme } from "@/types/look-system";
import { isColour, isName, record } from "@/utils/colour-values";
import { isLookSystem } from "@/utils/look-gate";

export interface PaletteHeld {
  /** What --re-p-<key> holds in the scheme. */
  colour: string;
  /** Whether that is the scheme's own version of the colour, not the other scheme's. */
  own: boolean;
}

/**
 * --re-p-<key> per scheme: the base scheme's `hex`; in dark `derived.darkPalette[key]`, else a chosen `darkHex`; in
 * light on a dark-first website `derived.lightPalette[key]`. `darkDeclared` is what the dark rule sets, undefined
 * where it inherits the light rule's colour.
 */
export function paletteSchemes(
  colorSystem: unknown,
  entry: unknown,
):
  | {
      key: string;
      light: PaletteHeld;
      dark: PaletteHeld;
      darkDeclared?: string;
    }
  | undefined {
  const { key, hex, darkHex } = record(entry);
  if (!isName(key) || !isColour(hex)) return undefined;
  const system = record(colorSystem);
  const darkFirst = system.base === "dark";
  const version = (scheme: LookScheme) => {
    const value = record(record(system.derived)[`${scheme}Palette`])[key];
    return isColour(value) ? value : undefined;
  };
  const lightVersion = darkFirst ? version("light") : hex;
  const light = lightVersion ?? hex;
  const chosenDark =
    version("dark") ?? (isColour(darkHex) ? darkHex : undefined);
  const darkVersion = chosenDark ?? (darkFirst ? hex : undefined);
  return {
    key,
    light: { colour: light, own: lightVersion !== undefined },
    dark: { colour: darkVersion ?? light, own: darkVersion !== undefined },
    darkDeclared: chosenDark ?? (darkFirst && light !== hex ? hex : undefined),
  };
}

const OWN_CODE = /^[0-9a-f]{6}$/;

/** `derived.darkOwn` as the dark rule's --re-o-<rrggbb>: each own colour code's dark version. */
export function ownColourDarkVars(
  colorSystem: unknown,
): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [code, value] of Object.entries(
    record(record(record(colorSystem).derived).darkOwn),
  ))
    if (OWN_CODE.test(code) && isColour(value)) vars[`re-o-${code}`] = value;
  return vars;
}

const OWN_HEX = /^#[0-9a-f]{6}$/i;
const ALPHA_HEX = /^#([0-9a-f]{4}|[0-9a-f]{8})$/i;
const SEE_THROUGH =
  /^(color-mix\(in srgb,\s*)(#[0-9a-f]{3,6})(\s+[\d.]+%,\s*transparent\))$/i;
const RGB_ALPHA =
  /^rgba?\(\s*(\d+(?:\.\d+)?)[\s,]+(\d+(?:\.\d+)?)[\s,]+(\d+(?:\.\d+)?)\s*[,/]\s*(\d*\.?\d+)(%?)\s*\)$/i;

const code = (hex: string) => hex.slice(1).toLowerCase();

const channelHex = (channel: string) =>
  Math.round(Math.min(255, Number(channel)))
    .toString(16)
    .padStart(2, "0");

interface Translucent {
  hex: string;
  percent: number;
  /** The code's own alpha digits, for a 4- or 8-digit code. */
  alphaDigits?: string;
}

/** rgb()/rgba() or a 4- or 8-digit code with an alpha below 1: its `#rrggbb` and that alpha in percent. */
function translucent(value: string): Translucent | undefined {
  const rgb = RGB_ALPHA.exec(value);
  const digits = ALPHA_HEX.exec(value)?.[1];
  const full =
    digits?.length === 4
      ? [...digits].map((digit) => digit + digit).join("")
      : digits;
  const alpha = rgb
    ? Number(rgb[4]) / (rgb[5] ? 100 : 1)
    : full
      ? parseInt(full.slice(6), 16) / 255
      : 1;
  if (!(alpha < 1)) return undefined;
  const hex = rgb
    ? `#${[rgb[1], rgb[2], rgb[3]].map(channelHex).join("")}`
    : `#${full!.slice(0, 6)}`;
  return {
    hex,
    percent: Number((alpha * 100).toFixed(2)),
    alphaDigits: full?.slice(6),
  };
}

/** `value` with the own colour code in it written by `write`; undefined where `derived.darkOwn` names no code in it. */
function rewriteOwn(
  theme: unknown,
  value: string,
  write: (code: string, dark: string) => string,
  seeThrough = (inner: string, seen: Translucent) =>
    `color-mix(in srgb, ${inner} ${seen.percent}%, transparent)`,
): string | undefined {
  if (!isLookSystem(theme)) return undefined;
  const darkOwn = record(record(theme.colorSystem.derived).darkOwn);
  const swapped = (hex: string) => {
    const own = code(hex);
    const dark = darkOwn[own];
    return OWN_CODE.test(own) && isColour(dark) ? write(own, dark) : undefined;
  };
  const text = value.trim();
  if (OWN_HEX.test(text)) return swapped(text);
  const mix = SEE_THROUGH.exec(text);
  if (mix) {
    const inner = OWN_HEX.test(mix[2]) ? swapped(mix[2]) : undefined;
    return inner && `${mix[1]}${inner}${mix[3]}`;
  }
  const seen = translucent(text);
  const inner = seen && swapped(seen.hex);
  return seen && inner ? seeThrough(inner, seen) : undefined;
}

/**
 * An own colour code (`#rrggbb`, as the CMS's ownHex counts it) in a colour field, also behind a Synlighed, as
 * `var(--re-o-<rrggbb>, #rrggbb)` where `derived.darkOwn` names its dark version, so the dark rule swaps it; any other
 * value, and every value off the look system, as it is. Like the CMS's `colourOpacity`, which decides what `darkOwn`
 * names, it reads the value trimmed and a colour with an alpha below 1 as its code at that Synlighed.
 */
export function ownColourCss(theme: unknown, value: string): string {
  return (
    rewriteOwn(theme, value, (own) => `var(--re-o-${own}, #${own})`) ?? value
  );
}

/** A dark version at a see-through colour's alpha, in that colour's own syntax, which the tone parsers read. */
function darkAtAlpha(dark: string, seen: Translucent): string {
  const digits = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(dark)?.[1];
  if (!digits)
    return `color-mix(in srgb, ${dark} ${seen.percent}%, transparent)`;
  const full =
    digits.length === 3
      ? [...digits].map((digit) => digit + digit).join("")
      : digits;
  if (seen.alphaDigits) return `#${full}${seen.alphaDigits}`;
  const [r, g, b] = full.match(/../g)!.map((pair) => parseInt(pair, 16));
  return `rgba(${r}, ${g}, ${b}, ${seen.percent / 100})`;
}

/**
 * What `ownColourCss` paints in dark, its code's dark version at the same Synlighed; undefined where it swaps nothing.
 * An rgb()/rgba() or 4- or 8-digit input gives it in that syntax, as the tone and ink computations read it.
 */
export function ownColourInDark(
  theme: unknown,
  value: string,
): string | undefined {
  return rewriteOwn(theme, value, (_, dark) => dark, darkAtAlpha);
}

const NO_FILL = ["", "transparent", "none", "inherit", "currentcolor"];

/** A palette or role colour, or an own colour code whose dark version `derived.darkOwn` names. */
export function fillChangesInDark(theme: unknown, fill: unknown): boolean {
  if (typeof fill !== "string") return false;
  const value = fill.trim().toLowerCase();
  if (NO_FILL.includes(value)) return false;
  return value.includes("var(--") || !!ownColourInDark(theme, fill);
}
